import { ArrowLeft } from "lucide-react";
import { Outlet, useSearchParams } from "react-router-dom";
import { useAuth } from "src/auth";
import { CircleSpinner, ModalBox, Skeleton } from "src/components";
import { RegisterInnerLayout } from "../Register/Layout";

import { useCallback } from "react";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { useStepperWithSearchParams } from "src/hooks/useStepper";
import RegisterIcon from "src/images/buy-property-icon.svg?react";
import LaterIcon from "src/images/later-icon.svg?react";

export function RegisterModalOutlet() {
  return (
    <div>
      <RegisterModal />
      <Outlet />
    </div>
  );
}

const RegisterModal = () => {
  const { user, registerPassword, isRegisteringPassword } = useAuth();

  const routes = [
    {
      path: "welcome",
      title: "Welcome to Fraction!",
      component: <ChooseWhatToDo />,
    },
    {
      path: "register",
      title: "Register your account",
      component: (
        <RegisterInnerLayout
          className="pt-4 px-4"
          isLoading={isRegisteringPassword}
          onSubmit={(submission) => {
            if (!submission?.password) {
              return;
            }
            registerPassword(submission?.password);
          }}
          user={user || undefined}
        />
      ),
    },
  ];

  return (
    <ModalBox closeButtonClassName="text-black" position="center center" open>
      {user ? (
        <Stepped routes={routes} />
      ) : (
        <div className="flex justify-center items-center h-full w-full bg-white">
          <CircleSpinner />
        </div>
      )}
    </ModalBox>
  );
};

const ChooseWhatToDo = () => {
  const [, setSearchParams] = useSearchParams();
  const { setItem } = useSessionStorage("register-later");
  const { user } = useAuth();

  const onClickRegister = useCallback(() => {
    console.log("onClickRegister");
    setSearchParams({ step: "register" });
  }, [setSearchParams]);

  const onClickLater = useCallback(() => {
    setItem("true");
  }, [setSearchParams]);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm mb-3">
        You clicked on a link for the email account{" "}
        {user?.email ? (
          <span className="text-green inline">{user?.email}</span>
        ) : (
          <Skeleton inline className="w-20 h-4" />
        )}
        . If you'd like to set up a password for this account, please register it now.
      </p>
      <button
        onClick={onClickRegister}
        className="outline-green w-full p-4 rounded-lg bg-green-50 hover:bg-green-100 border border-green-200 flex flex-row items-center gap-3"
      >
        <RegisterIcon
          className="md:min-w-[60px] md:min-h-[60px] min-w-[40px] min-h-[40px] hidden md:block"
          height="100%"
        />
        <div className="text-left">
          <h1 className="text-lg font-bold font-serif">Register your account</h1>
          <p className="text-sm font-normal text-green-900">
            Register your account with a password. This will allow you to log in and re-visit your
            application(s).
          </p>
        </div>
      </button>
      <button
        onClick={onClickLater}
        className="outline-green w-full p-4 rounded-lg bg-green-50 hover:bg-green-100 border border-green-200 flex flex-row items-center gap-3"
      >
        <LaterIcon
          className="md:min-w-[60px] md:min-h-[60px] min-w-[40px] min-h-[40px] hidden md:block"
          height="100%"
        />
        <div className="text-left">
          <h1 className="text-lg font-bold font-serif">Skip for now</h1>
          <p className="text-sm font-normal text-green-900">
            You can always register later by re-visiting the link you clicked to get here.
          </p>
        </div>
      </button>
    </div>
  );
};

const Stepped = ({ routes }: { routes: { path: string; title: string; component: React.ReactNode }[] }) => {
  const { step, previous, ...stepper } = useStepperWithSearchParams(
    routes.map((route) => route.path),
    "welcome"
  );

  return (
    <div className="p-8 py-7">
      <div className="mb-2">
        <h1 className="text-2xl font-serif font-bold">
          {previous ? (
            <button className="p-2 rounded-full bg-white hover:bg-gray-100 mr-1" onClick={stepper.goBack}>
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : null}
          {routes.find((route) => route.path === step)?.title}
        </h1>
      </div>
      {routes.find((route) => route.path === step)?.component}
    </div>
  );
};
